import {
    AgingBucketStatisticViewModel,
    CashflowSeriesPointViewModel,
    DebtSeriesPointViewModel,
    MonthlyCashflowStatisticViewModel,
    OverviewStatisticViewModel,
    PaymentDelaySeriesPointViewModel,
    PaymentDelayStatisticViewModel,
    StatisticsFilter,
    StatisticsTimeSeriesFilter,
    TopEntityStatisticViewModel
} from '../models/Entities/CashFlow/Statistics/StatisticsModels';
import { apiRequest } from './commons/CommonService.Functions';

// urls
const BaseURL = process.env.REACT_APP_BILLER_API_GATEWAY + 'cashflow/statistics';

// Build a query string from a filter object, skipping undefined / null / empty values.
const buildQuery = (filter: object | undefined): string => {
    if (!filter) return '';
    const sp = new URLSearchParams();
    Object.entries(filter as Record<string, unknown>).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') {
            sp.append(k, `${v as string | number | boolean}`);
        }
    });
    const qs = sp.toString();
    return qs ? `?${qs}` : '';
};

// ---------- KPIs / Indicators ----------

export const GetOverview = async (
    token: string,
    filter?: StatisticsFilter
): Promise<OverviewStatisticViewModel[]> => {
    const url = `${BaseURL}/overview${buildQuery(filter)}`;
    return apiRequest<OverviewStatisticViewModel[]>(url, token, 'GetOverview');
};

export const GetPaymentDelay = async (
    token: string,
    filter?: StatisticsFilter
): Promise<PaymentDelayStatisticViewModel[]> => {
    const url = `${BaseURL}/payment-delay${buildQuery(filter)}`;
    return apiRequest<PaymentDelayStatisticViewModel[]>(url, token, 'GetPaymentDelay');
};

export const GetMonthlyCashflow = async (
    token: string,
    filter?: StatisticsFilter
): Promise<MonthlyCashflowStatisticViewModel[]> => {
    const url = `${BaseURL}/monthly-cashflow${buildQuery(filter)}`;
    return apiRequest<MonthlyCashflowStatisticViewModel[]>(url, token, 'GetMonthlyCashflow');
};

export const GetTopDebtors = async (
    token: string,
    filter?: StatisticsFilter,
    top: number = 10
): Promise<TopEntityStatisticViewModel[]> => {
    const url = `${BaseURL}/top-debtors${buildQuery({ ...(filter as object || undefined), top })}`;
    return apiRequest<TopEntityStatisticViewModel[]>(url, token, 'GetTopDebtors');
};

export const GetTopCreditors = async (
    token: string,
    filter?: StatisticsFilter,
    top: number = 10
): Promise<TopEntityStatisticViewModel[]> => {
    const url = `${BaseURL}/top-creditors${buildQuery({ ...(filter as object || undefined), top })}`;
    return apiRequest<TopEntityStatisticViewModel[]>(url, token, 'GetTopCreditors');
};

export const GetAging = async (
    token: string,
    filter?: StatisticsFilter
): Promise<AgingBucketStatisticViewModel[]> => {
    const url = `${BaseURL}/aging${buildQuery(filter)}`;
    return apiRequest<AgingBucketStatisticViewModel[]>(url, token, 'GetAging');
};

// ---------- Time series (charts) ----------

export const GetCashflowSeries = async (
    token: string,
    filter?: StatisticsTimeSeriesFilter
): Promise<CashflowSeriesPointViewModel[]> => {
    const url = `${BaseURL}/series/cashflow${buildQuery(filter)}`;
    return apiRequest<CashflowSeriesPointViewModel[]>(url, token, 'GetCashflowSeries');
};

export const GetDebtSeries = async (
    token: string,
    filter?: StatisticsTimeSeriesFilter
): Promise<DebtSeriesPointViewModel[]> => {
    const url = `${BaseURL}/series/debt${buildQuery(filter)}`;
    return apiRequest<DebtSeriesPointViewModel[]>(url, token, 'GetDebtSeries');
};

export const GetPaymentDelaySeries = async (
    token: string,
    filter?: StatisticsTimeSeriesFilter
): Promise<PaymentDelaySeriesPointViewModel[]> => {
    const url = `${BaseURL}/series/payment-delay${buildQuery(filter)}`;
    return apiRequest<PaymentDelaySeriesPointViewModel[]>(url, token, 'GetPaymentDelaySeries');
};
